import { APIMethods, apiProxy, Message, TResponse } from 'utils/api/apiRequestProxy';
import { QueryFunctionContext } from 'react-query/types/core/types';
import { IProfileUser, IUser } from 'types/IUser';
import { ICSVUploadedData } from './ImportUsers/types';
import { IGetUsersQuery, ReqAddUser, ReqUploadUsers } from './types';

export async function requestBulkUploadUsers(data: ReqUploadUsers): Promise<TResponse<ICSVUploadedData>> {
    return apiProxy<ICSVUploadedData>({
        url: '/users/bulk_upload/',
        method: APIMethods.POST,
        data,
    });
}

export async function fetchUser({
    queryKey,
}: QueryFunctionContext<[string, { userId: string; companyId?: string }]>): Promise<TResponse<IUser>> {
    const [, { userId, companyId }] = queryKey;
    return apiProxy<IUser>({
        url: `/user/${userId}`,
        method: APIMethods.GET,
        params: { company_id: companyId },
    });
}

export async function fetchUsers({ queryKey }: QueryFunctionContext<[string, IGetUsersQuery]>): Promise<TResponse<IUser[]>> {
    const [, query] = queryKey;
    return apiProxy<IUser[]>({
        url: `/users/`,
        method: APIMethods.GET,
        params: query,
        respKey: 'users',
    });
}

export type TWalletAllocationStatus = 'allocated' | 'skipped';

export type TWalletAllocationResp = {
    status: TWalletAllocationStatus;
    reason?: 'insufficient_passes' | 'no_active_plan' | 'already_allocated' | 'internal_error';
    message?: string;
};

export type TAddUserResp = Message & {
    id?: number;
    id_str?: string;
    wallet_allocation?: TWalletAllocationResp;
};

export async function requestAddUsers(data: ReqAddUser): Promise<TResponse<TAddUserResp>> {
    return apiProxy<TAddUserResp>({
        url: '/users/',
        method: APIMethods.POST,
        showToastSuccessMsg: false,
        data,
    });
}

export async function requestGerUserProfile(): Promise<TResponse<IProfileUser>> {
    return apiProxy<IProfileUser>({
        url: '/profile/',
        method: APIMethods.GET,
        respKey: 'user',
    });
}

export async function requestUploadProfilePhoto({ userId, file }: { userId?: string; file: File }): Promise<TResponse<Message>> {
    const data = new FormData();
    data.append('file', file);

    return apiProxy<Message>({
        url: `/profile_photo/`,
        method: APIMethods.POST,
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        params: { user_id: userId },
        data,
    });
}

export async function requestDeleteProfilePhoto({ userId }: { userId?: string }): Promise<TResponse<Message>> {
    return apiProxy<Message>({
        url: `/profile_photo/`,
        method: APIMethods.DELETE,
        params: { user_id: userId },
    });
}

export async function requestBulkDisableUserPins(userIds: string[]): Promise<TResponse<Message>> {
    return apiProxy<Message>({
        url: '/user_pin/bulk-disable',
        method: APIMethods.POST,
        data: { user_id_list: userIds },
    });
}

export async function requestExcludeUserFromSync(id: string): Promise<TResponse<Message>> {
    return apiProxy<Message>({
        url: `/users/${id}/exclude-from-sync`,
        method: APIMethods.POST,
    });
}

export async function requestEnableUserSync(id: string): Promise<TResponse<Message>> {
    return apiProxy<Message>({
        url: `/users/${id}/enable-sync`,
        method: APIMethods.POST,
    });
}
